/**
 * useAdminMetrics Hook
 *
 * Provides comprehensive platform metrics for admin dashboard
 * by querying real Firestore collections.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/services/firebase';

export interface AdminMetrics {
  users: {
    total: number;
    active: number;
    newThisMonth: number;
    byRole: {
      guest: number;
      host: number;
      admin: number;
    };
  };
  listings: {
    total: number;
    active: number;
    pending: number;
    newThisMonth: number;
    byType: Record<string, number>;
    byLocation: Record<string, number>;
  };
  bookings: {
    total: number;
    confirmed: number;
    pending: number;
    completed: number;
    cancelled: number;
    cancelledRate: number;
  };
  revenue: {
    totalGross: number;
    platformRevenue: number;
    thisMonth: number;
    lastMonth: number;
    growth: number;
    averageBookingValue: number;
  };
  payouts: {
    totalPaid: number;
    pending: number;
    pendingAmount: number;
    thisMonth: number;
  };
}

export type DateRange = 'today' | '7days' | '30days' | '90days' | 'year' | 'all';

const getDateRangeStart = (range: DateRange): Date | null => {
  const now = new Date();
  switch (range) {
    case 'today':
      return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    case '7days':
      return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    case '30days':
      return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    case '90days':
      return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
    case 'year':
      return new Date(now.getFullYear(), 0, 1);
    case 'all':
      return null;
  }
};

export const useAdminMetrics = (dateRange: DateRange = '30days') => {
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      getDateRangeStart(dateRange);

      // Fetch all collections in parallel
      const [usersSnap, listingsSnap, bookingsSnap, earningsSnap, payoutsSnap] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'listings')),
        getDocs(collection(db, 'bookings')),
        getDocs(collection(db, 'earnings')),
        getDocs(collection(db, 'payouts')),
      ]);

      // --- Users ---
      const users = usersSnap.docs.map((d) => d.data());
      const userMetrics = {
        total: users.length,
        active: users.filter((u) => {
          const lastLogin = u.lastLoginAt instanceof Timestamp ? u.lastLoginAt.toDate() : u.lastLoginAt;
          return lastLogin && lastLogin > thirtyDaysAgo;
        }).length,
        newThisMonth: users.filter((u) => {
          const created = u.createdAt instanceof Timestamp ? u.createdAt.toDate() : u.createdAt;
          return created && created > startOfMonth;
        }).length,
        byRole: {
          guest: users.filter((u) => u.role === 'guest').length,
          host: users.filter((u) => u.role === 'host').length,
          admin: users.filter((u) => u.role === 'admin').length,
        },
      };

      // --- Listings ---
      const listings = listingsSnap.docs.map((d) => d.data());
      const byType: Record<string, number> = {};
      const byLocation: Record<string, number> = {};
      listings.forEach((l) => {
        const pType = l.propertyType || 'other';
        byType[pType] = (byType[pType] || 0) + 1;
        const loc = l.location?.state || l.location?.area || l.location?.city || 'Unknown';
        byLocation[loc] = (byLocation[loc] || 0) + 1;
      });

      const listingMetrics = {
        total: listings.length,
        active: listings.filter((l) => l.status === 'published').length,
        pending: listings.filter((l) => l.status === 'pending').length,
        newThisMonth: listings.filter((l) => {
          const created = l.createdAt instanceof Timestamp ? l.createdAt.toDate() : l.createdAt;
          return created && created > startOfMonth;
        }).length,
        byType,
        byLocation,
      };

      // --- Bookings ---
      const bookings = bookingsSnap.docs.map((d) => d.data());
      const cancelledCount = bookings.filter((b) => b.status === 'cancelled').length;
      const bookingMetrics = {
        total: bookings.length,
        confirmed: bookings.filter((b) => b.status === 'confirmed').length,
        pending: bookings.filter((b) => b.status === 'pending').length,
        completed: bookings.filter((b) => b.status === 'completed').length,
        cancelled: cancelledCount,
        cancelledRate: bookings.length > 0 ? (cancelledCount / bookings.length) * 100 : 0,
      };

      // --- Revenue ---
      const earnings = earningsSnap.docs.map((d) => d.data());
      const activeEarnings = earnings.filter((e) => e.status !== 'cancelled');
      const thisMonthEarnings = activeEarnings.filter((e) => {
        const created = e.createdAt instanceof Timestamp ? e.createdAt.toDate() : e.createdAt;
        return created && created > startOfMonth;
      });
      const lastMonthEarnings = activeEarnings.filter((e) => {
        const created = e.createdAt instanceof Timestamp ? e.createdAt.toDate() : e.createdAt;
        return created && created > startOfLastMonth && created <= startOfMonth;
      });

      const thisMonthRevenue = thisMonthEarnings.reduce(
        (sum, e) => sum + (e.platformCommission || 0), 0
      );
      const lastMonthRevenue = lastMonthEarnings.reduce(
        (sum, e) => sum + (e.platformCommission || 0), 0
      );
      const totalGross = activeEarnings.reduce((sum, e) => sum + (e.grossAmount || 0), 0);
      const platformRevenue = activeEarnings.reduce(
        (sum, e) => sum + (e.platformCommission || 0), 0
      );

      const revenueMetrics = {
        totalGross,
        platformRevenue,
        thisMonth: thisMonthRevenue,
        lastMonth: lastMonthRevenue,
        growth: lastMonthRevenue > 0
          ? ((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100
          : 0,
        averageBookingValue: bookings.length > 0
          ? totalGross / bookings.length
          : 0,
      };

      // --- Payouts ---
      const payouts = payoutsSnap.docs.map((d) => d.data());
      const completedPayouts = payouts.filter((p) => p.status === 'completed');
      const pendingPayouts = payouts.filter((p) => p.status === 'requested');
      const thisMonthPayouts = completedPayouts.filter((p) => {
        const completed = p.completedAt instanceof Timestamp ? p.completedAt.toDate() : p.completedAt;
        return completed && completed > startOfMonth;
      });

      const payoutMetrics = {
        totalPaid: completedPayouts.reduce((sum, p) => sum + (p.amount || 0), 0),
        pending: pendingPayouts.length,
        pendingAmount: pendingPayouts.reduce((sum, p) => sum + (p.amount || 0), 0),
        thisMonth: thisMonthPayouts.reduce((sum, p) => sum + (p.amount || 0), 0),
      };

      setMetrics({
        users: userMetrics,
        listings: listingMetrics,
        bookings: bookingMetrics,
        revenue: revenueMetrics,
        payouts: payoutMetrics,
      });
    } catch (err) {
      console.error('Failed to fetch admin metrics:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch metrics');
    } finally {
      setIsLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return {
    metrics,
    isLoading,
    error,
    refetch: fetchMetrics,
  };
};

export default useAdminMetrics;
